import React from 'react';
import { Code, Zap, Database, Key, AlertCircle, CheckCircle, XCircle } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card';
import Badge from '../components/ui/Badge';

const APIReference = () => {
  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center py-8">
        <div className="w-16 h-16 bg-reddit-orange rounded-full flex items-center justify-center mx-auto mb-4">
          <Code className="w-8 h-8 text-white" />
        </div>
        <h1 className="text-4xl font-bold mb-4">API Reference</h1>
        <p className="text-lg text-reddit-600 dark:text-dark-textMuted max-w-3xl mx-auto">
          Complete API documentation for integrating with Trendy's data services
        </p>
        <p className="text-sm text-reddit-600 dark:text-dark-textMuted mt-2">
          Version 1.0.0 | Last Updated: January 27, 2026
        </p>
      </div>

      {/* Quick Start */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="w-6 h-6 text-reddit-orange" />
            Quick Start
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p>Get started with the Trendy API in minutes. Follow these simple steps:</p>
          
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-reddit-orange text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm">
                1
              </div>
              <div>
                <h4 className="font-semibold">Set up your environment</h4>
                <p className="text-sm text-reddit-600 dark:text-dark-textMuted">
                  Copy <code className="bg-reddit-100 dark:bg-dark-border px-2 py-1 rounded">.env.example</code> to <code className="bg-reddit-100 dark:bg-dark-border px-2 py-1 rounded">.env.local</code>
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-reddit-orange text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm">
                2
              </div>
              <div>
                <h4 className="font-semibold">Add API credentials</h4>
                <p className="text-sm text-reddit-600 dark:text-dark-textMuted">
                  Configure your API keys in the environment file
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-reddit-orange text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm">
                3
              </div>
              <div>
                <h4 className="font-semibold">Start making requests</h4>
                <p className="text-sm text-reddit-600 dark:text-dark-textMuted">
                  Use the endpoints documented below to access trending data
                </p>
              </div>
            </div>
          </div>

          <div className="bg-reddit-50 dark:bg-dark-border p-4 rounded-lg mt-4">
            <h4 className="font-semibold mb-2">Base URL</h4>
            <code className="text-sm">https://api.trendy.com/v1</code>
          </div>
        </CardContent>
      </Card>

      {/* Authentication */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="w-6 h-6 text-reddit-blue" />
            Authentication
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p>Trendy uses API keys for authentication. Include your API key in the request header:</p>
          
          <div className="bg-dark-bg text-dark-text p-4 rounded-lg font-mono text-sm overflow-x-auto">
            <div className="text-reddit-green">// Request Header</div>
            <div className="text-reddit-blue">Authorization: <span className="text-reddit-orange">Bearer YOUR_API_KEY</span></div>
          </div>

          <div className="bg-reddit-50 dark:bg-dark-border p-4 rounded-lg">
            <h4 className="font-semibold mb-2 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-reddit-orange" />
              Important
            </h4>
            <p className="text-sm">
              Keep your API keys secure. Never expose them in client-side code or public repositories.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Endpoints */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="w-6 h-6 text-reddit-green" />
            API Endpoints
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Get Trends */}
          <div className="border-l-4 border-reddit-orange pl-4">
            <div className="flex items-center gap-2 mb-2">
              <Badge variant="green">GET</Badge>
              <code className="text-sm">/trends</code>
            </div>
            <p className="text-sm text-reddit-600 dark:text-dark-textMuted mb-3">
              Retrieve trending topics based on specified filters
            </p>
            
            <div className="space-y-3">
              <div>
                <h5 className="font-semibold text-sm mb-2">Query Parameters</h5>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-reddit-200 dark:border-dark-border">
                        <th className="text-left p-2">Parameter</th>
                        <th className="text-left p-2">Type</th>
                        <th className="text-left p-2">Required</th>
                        <th className="text-left p-2">Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-reddit-100 dark:border-dark-border">
                        <td className="p-2"><code>category</code></td>
                        <td className="p-2">string</td>
                        <td className="p-2">No</td>
                        <td className="p-2">Filter by category (technology, politics, sports, etc.)</td>
                      </tr>
                      <tr className="border-b border-reddit-100 dark:border-dark-border">
                        <td className="p-2"><code>region</code></td>
                        <td className="p-2">string</td>
                        <td className="p-2">No</td>
                        <td className="p-2">Filter by region (global, north-america, europe, etc.)</td>
                      </tr>
                      <tr className="border-b border-reddit-100 dark:border-dark-border">
                        <td className="p-2"><code>source</code></td>
                        <td className="p-2">string</td>
                        <td className="p-2">No</td>
                        <td className="p-2">Filter by source (twitter, reddit, google-trends)</td>
                      </tr>
                      <tr>
                        <td className="p-2"><code>limit</code></td>
                        <td className="p-2">number</td>
                        <td className="p-2">No</td>
                        <td className="p-2">Maximum number of results (default: 20, max: 100)</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <h5 className="font-semibold text-sm mb-2">Example Request</h5>
                <div className="bg-dark-bg text-dark-text p-4 rounded-lg font-mono text-sm overflow-x-auto">
                  <div className="text-reddit-blue">GET <span className="text-reddit-orange">/trends?category=technology&limit=10</span></div>
                </div>
              </div>

              <div>
                <h5 className="font-semibold text-sm mb-2">Example Response</h5>
                <div className="bg-dark-bg text-dark-text p-4 rounded-lg font-mono text-sm overflow-x-auto">
                  <pre>{`{
  "data": [
    {
      "id": 1,
      "topic": "AI Revolution",
      "mentions": 125430,
      "growth": 45.2,
      "category": "Technology",
      "region": "Global",
      "sentiment": 0.78,
      "source": "Twitter",
      "timestamp": "2026-01-27T10:30:00Z"
    }
  ],
  "total": 42,
  "page": 1,
  "limit": 10
}`}</pre>
                </div>
              </div>
            </div>
          </div>

          {/* Get Trend by ID */}
          <div className="border-l-4 border-reddit-blue pl-4">
            <div className="flex items-center gap-2 mb-2">
              <Badge variant="green">GET</Badge>
              <code className="text-sm">/trends/:id</code>
            </div>
            <p className="text-sm text-reddit-600 dark:text-dark-textMuted mb-3">
              Get detailed information about a specific trend
            </p>
            
            <div className="space-y-3">
              <div>
                <h5 className="font-semibold text-sm mb-2">Path Parameters</h5>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-reddit-200 dark:border-dark-border">
                        <th className="text-left p-2">Parameter</th>
                        <th className="text-left p-2">Type</th>
                        <th className="text-left p-2">Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="p-2"><code>id</code></td>
                        <td className="p-2">number</td>
                        <td className="p-2">Unique trend identifier</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <h5 className="font-semibold text-sm mb-2">Example Request</h5>
                <div className="bg-dark-bg text-dark-text p-4 rounded-lg font-mono text-sm overflow-x-auto">
                  <div className="text-reddit-blue">GET <span className="text-reddit-orange">/trends/123</span></div>
                </div>
              </div>
            </div>
          </div>

          {/* Search Trends */}
          <div className="border-l-4 border-reddit-green pl-4">
            <div className="flex items-center gap-2 mb-2">
              <Badge variant="green">GET</Badge>
              <code className="text-sm">/search</code>
            </div>
            <p className="text-sm text-reddit-600 dark:text-dark-textMuted mb-3">
              Search for trends by keyword
            </p>
            
            <div className="space-y-3">
              <div>
                <h5 className="font-semibold text-sm mb-2">Query Parameters</h5>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-reddit-200 dark:border-dark-border">
                        <th className="text-left p-2">Parameter</th>
                        <th className="text-left p-2">Type</th>
                        <th className="text-left p-2">Required</th>
                        <th className="text-left p-2">Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-reddit-100 dark:border-dark-border">
                        <td className="p-2"><code>q</code></td>
                        <td className="p-2">string</td>
                        <td className="p-2">Yes</td>
                        <td className="p-2">Search query</td>
                      </tr>
                      <tr>
                        <td className="p-2"><code>limit</code></td>
                        <td className="p-2">number</td>
                        <td className="p-2">No</td>
                        <td className="p-2">Maximum number of results (default: 20)</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <h5 className="font-semibold text-sm mb-2">Example Request</h5>
                <div className="bg-dark-bg text-dark-text p-4 rounded-lg font-mono text-sm overflow-x-auto">
                  <div className="text-reddit-blue">GET <span className="text-reddit-orange">/search?q=artificial+intelligence</span></div>
                </div>
              </div>
            </div>
          </div>

          {/* Get Heatmap Data */}
          <div className="border-l-4 border-orange-500 pl-4">
            <div className="flex items-center gap-2 mb-2">
              <Badge variant="green">GET</Badge>
              <code className="text-sm">/heatmap</code>
            </div>
            <p className="text-sm text-reddit-600 dark:text-dark-textMuted mb-3">
              Get regional trending data for heatmap visualization
            </p>
            
            <div className="space-y-3">
              <div>
                <h5 className="font-semibold text-sm mb-2">Example Request</h5>
                <div className="bg-dark-bg text-dark-text p-4 rounded-lg font-mono text-sm overflow-x-auto">
                  <div className="text-reddit-blue">GET <span className="text-reddit-orange">/heatmap</span></div>
                </div>
              </div>
            </div>
          </div>

          {/* Get Analytics */}
          <div className="border-l-4 border-purple-500 pl-4">
            <div className="flex items-center gap-2 mb-2">
              <Badge variant="green">GET</Badge>
              <code className="text-sm">/analytics</code>
            </div>
            <p className="text-sm text-reddit-600 dark:text-dark-textMuted mb-3">
              Get analytics summary and statistics
            </p>
            
            <div className="space-y-3">
              <div>
                <h5 className="font-semibold text-sm mb-2">Query Parameters</h5>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-reddit-200 dark:border-dark-border">
                        <th className="text-left p-2">Parameter</th>
                        <th className="text-left p-2">Type</th>
                        <th className="text-left p-2">Required</th>
                        <th className="text-left p-2">Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-reddit-100 dark:border-dark-border">
                        <td className="p-2"><code>period</code></td>
                        <td className="p-2">string</td>
                        <td className="p-2">No</td>
                        <td className="p-2">Time period (7d, 30d, 90d, 1y)</td>
                      </tr>
                      <tr>
                        <td className="p-2"><code>region</code></td>
                        <td className="p-2">string</td>
                        <td className="p-2">No</td>
                        <td className="p-2">Filter by region</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Response Codes */}
      <Card>
        <CardHeader>
          <CardTitle>Response Codes</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-reddit-green" />
              <div>
                <code className="font-bold">200 OK</code>
                <p className="text-sm text-reddit-600 dark:text-dark-textMuted">Request successful</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-orange-500" />
              <div>
                <code className="font-bold">400 Bad Request</code>
                <p className="text-sm text-reddit-600 dark:text-dark-textMuted">Invalid request parameters</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <XCircle className="w-5 h-5 text-reddit-red" />
              <div>
                <code className="font-bold">401 Unauthorized</code>
                <p className="text-sm text-reddit-600 dark:text-dark-textMuted">Missing or invalid API key</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <XCircle className="w-5 h-5 text-reddit-red" />
              <div>
                <code className="font-bold">404 Not Found</code>
                <p className="text-sm text-reddit-600 dark:text-dark-textMuted">Resource not found</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <XCircle className="w-5 h-5 text-reddit-red" />
              <div>
                <code className="font-bold">429 Too Many Requests</code>
                <p className="text-sm text-reddit-600 dark:text-dark-textMuted">Rate limit exceeded</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <XCircle className="w-5 h-5 text-reddit-red" />
              <div>
                <code className="font-bold">500 Internal Server Error</code>
                <p className="text-sm text-reddit-600 dark:text-dark-textMuted">Server error occurred</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Rate Limiting */}
      <Card>
        <CardHeader>
          <CardTitle>Rate Limiting</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p>To ensure fair usage and system stability, we implement rate limiting:</p>
          
          <div className="bg-reddit-50 dark:bg-dark-border p-4 rounded-lg">
            <ul className="list-disc list-inside space-y-1 text-sm">
              <li><strong>Free tier:</strong> 100 requests per hour</li>
              <li><strong>Pro tier:</strong> 1,000 requests per hour</li>
              <li><strong>Enterprise tier:</strong> Custom limits</li>
            </ul>
          </div>

          <p className="text-sm text-reddit-600 dark:text-dark-textMuted">
            Rate limit information is included in response headers:
          </p>
          
          <div className="bg-dark-bg text-dark-text p-4 rounded-lg font-mono text-sm overflow-x-auto">
            <div>X-RateLimit-Limit: 100</div>
            <div>X-RateLimit-Remaining: 95</div>
            <div>X-RateLimit-Reset: 1706356800</div>
          </div>
        </CardContent>
      </Card>

      {/* Error Handling */}
      <Card>
        <CardHeader>
          <CardTitle>Error Handling</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p>All errors follow a consistent format:</p>
          
          <div className="bg-dark-bg text-dark-text p-4 rounded-lg font-mono text-sm overflow-x-auto">
            <pre>{`{
  "error": {
    "code": "INVALID_PARAMETER",
    "message": "Invalid category specified",
    "details": {
      "field": "category",
      "allowed_values": ["technology", "politics", "sports"]
    }
  }
}`}</pre>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default APIReference;
